import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { CommunityAuthor } from '@/shared/composables/community/profile';
import { userCookies } from '@/shared/constants/constants';
import { loyaltyBalanceQuery } from '@/shared/graphql/gateway/queries';
import { getGatewayApolloClient } from '@/shared/utils/apollo-client';
import { setCookie } from '@/shared/utils/cookie';
import { parseJwt } from '@/shared/utils/jwt';
import { hobbiiLocalStorage } from '@/shared/utils/web-storage';

// TODO: Could this be determined by the creating service?
export type Customer = {
  uuid: string;
  first_name?: string;
  last_name?: string;
  email: string;
};

type OwnProfile = Omit<CommunityAuthor, 'is_following' | 'is_blocking'>;

// Guest-side staging for details captured across multiple entrypoints
// (pattern download, footer newsletter, My Hobbii nudge, etc.), until the
// backend write path lands. Deliberately separate from `customer`, which is
// derived from an auth JWT and doesn't exist for guests.
export type CraftName = 'Knit' | 'Crochet';
export type SkillLevel = 'Beginner' | 'Intermediate' | 'Experienced';

export type CraftSelection = {
  name: CraftName;
  skillLevel: SkillLevel;
};

export type CapturedProfile = {
  firstName: string;
  lastName: string;
  email: string;
  crafts: CraftSelection[];
};

const isValidCapturedProfile = (data: unknown): data is CapturedProfile => {
  if (typeof data !== 'object' || data === null) return false;

  const candidate = data as Record<string, unknown>;
  return (
    typeof candidate.firstName === 'string' &&
    typeof candidate.lastName === 'string' &&
    typeof candidate.email === 'string' &&
    Array.isArray(candidate.crafts)
  );
};

// A customer is identified by uuid and email. Name claims are absent for
// customers created through express checkout, so requiring them here would
// resolve a signed-in customer to null and sign them out.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidCustomer = (data: any): data is Customer =>
  data && typeof data.uuid === 'string' && typeof data.email === 'string';

export const useUserStore = defineStore('user', () => {
  const pointsBalance = ref<number | null>(null);
  const _persistedToken = ref<string | null>(
    hobbiiLocalStorage.getItem(userCookies.C_ID)
  );

  const _token = computed<string | null>({
    set: (value: string | null) => {
      _persistedToken.value = value;
      if (value) {
        hobbiiLocalStorage.setItem(userCookies.C_ID, value);
      } else {
        hobbiiLocalStorage.removeItem(userCookies.C_ID);
        setCookie(userCookies.C_ID, '', -5);
      }
    },
    get: () => _persistedToken.value
  });

  const customer = computed<Customer | null>(() => {
    if (!_token.value) {
      return null;
    }
    const obj = parseJwt(_token.value);

    if (!obj) {
      console.error('Could not read customer data from token');
      return null;
    }

    const customerData = {
      uuid: obj['cognito:username'],
      first_name: obj.name,
      last_name: obj.family_name,
      email: obj.email
    };

    if (isValidCustomer(customerData)) {
      return customerData;
    }

    console.error('Incomplete customer data from token');
    return null;
  });

  const authenticated = computed(() => !!customer.value);
  /**
   * @deprecated Use authenticated instead
   */
  const isLogged = computed(() => authenticated.value);

  const setCustomer = (token?: string) => {
    if (token) {
      _token.value = token;
    } else {
      cleanCustomer();
    }
  };

  const cleanCustomer = () => {
    _token.value = null;
    _communityProfile.value = null;
  };

  const getCustomerPointsBalance = async () => {
    if (pointsBalance.value !== null) {
      return pointsBalance.value;
    }

    // A failed read must reject rather than resolve to null — null means
    // "skip the check" to validateUserHasEnoughPoints, which would silently
    // let a points product through instead of blocking the add.
    const { data } = await getGatewayApolloClient().query({
      query: loyaltyBalanceQuery
    });

    const balance = data?.loyalty?.user?.balance;

    // 0 is a real balance and must reach the caller, so test the type rather
    // than the truthiness.
    if (typeof balance !== 'number') return null;

    pointsBalance.value = balance;
    return pointsBalance.value;
  };

  // TODO: Temp solution, while userStore & apolloClient is still tangled
  const _communityProfile = ref<OwnProfile | null>();
  const communityProfile = computed(() => _communityProfile.value);
  const setCommunityProfile = (profile: OwnProfile) => {
    _communityProfile.value = profile;
  };

  // ToS (ToC)
  const _termsOfServiceAgreed = ref<boolean | null>(null);
  const setTermsOfServiceAgreement = (agreed: boolean) => {
    _termsOfServiceAgreed.value = agreed;
    _showTermsOfServiceAgreementModal.value = false;
  };

  const _showTermsOfServiceAgreementModal = ref(false);
  const showingTermsOfServiceAgreementModal = computed(
    () => _showTermsOfServiceAgreementModal.value
  );

  const openAndAwaitTermsOfServiceAgreementModal = () => {
    _showTermsOfServiceAgreementModal.value = true;

    return new Promise((resolve) => {
      // watch for one change
      const stopWatching = watch(
        _showTermsOfServiceAgreementModal,
        (showing) => {
          if (!showing) {
            stopWatching();
            resolve(_termsOfServiceAgreed.value);
          }
        }
      );
    });
  };

  const closeTermsOfServiceAgreementModal = () => {
    _showTermsOfServiceAgreementModal.value = false;
  };

  // Account-blocked notice — fired when a community write returns a
  // USER_BLOCKED GraphQL error (see shared/graphql/gateway/utils.ts).
  // Unlike TOS this is informational only; no retry, no awaiting agreement.
  const _showAccountBlockedNoticeModal = ref(false);
  const showingAccountBlockedNoticeModal = computed(
    () => _showAccountBlockedNoticeModal.value
  );

  const notifyAccountBlocked = () => {
    _showAccountBlockedNoticeModal.value = true;
  };

  const closeAccountBlockedNoticeModal = () => {
    _showAccountBlockedNoticeModal.value = false;
  };

  const _persistedCapturedProfile = hobbiiLocalStorage.getItem(
    userCookies.CAPTURED_PROFILE
  );
  const _capturedProfile = ref<CapturedProfile | null>(
    _persistedCapturedProfile ? JSON.parse(_persistedCapturedProfile) : null
  );
  const capturedProfile = computed(() => _capturedProfile.value);

  const setCapturedProfile = (profile: CapturedProfile) => {
    if (!isValidCapturedProfile(profile)) {
      console.error('Invalid captured profile data');
      return;
    }

    _capturedProfile.value = profile;
    hobbiiLocalStorage.setItem(
      userCookies.CAPTURED_PROFILE,
      JSON.stringify(profile)
    );
  };

  const clearCapturedProfile = () => {
    _capturedProfile.value = null;
    hobbiiLocalStorage.removeItem(userCookies.CAPTURED_PROFILE);
  };

  return {
    authenticated,
    isLogged,
    customer,
    setCustomer,
    cleanCustomer,
    communityProfile,
    setCommunityProfile,
    openAndAwaitTermsOfServiceAgreementModal,
    showingTermsOfServiceAgreementModal,
    closeTermsOfServiceAgreementModal,
    setTermsOfServiceAgreement,
    notifyAccountBlocked,
    showingAccountBlockedNoticeModal,
    closeAccountBlockedNoticeModal,
    getCustomerPointsBalance,
    capturedProfile,
    setCapturedProfile,
    clearCapturedProfile
  };
});
